import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search } from "lucide-react";

import { AdminScreen, Field } from "@/admin/kit";
import { api } from "@/lib/api";
import { Button, Spinner, Tag } from "@/components/kit";
import { useApp } from "@/store";

export default function AdminHairstyles() {
  const navigate = useNavigate();
  const { toast } = useApp();
  const [q, setQ] = useState("");
  const [items, setItems] = useState(null);
  const [newName, setNewName] = useState("");
  const load = useCallback(() => {
    api(`/admin/hairstyles?q=${encodeURIComponent(q)}`).then(setItems).catch((e) => toast(e.message, "error"));
  }, [q, toast]);
  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load]);
  const create = async () => {
    try {
      const s = await api("/admin/hairstyles", { method: "POST", body: { name: newName.trim() } });
      setNewName("");
      navigate(`/admin/hairstyle/${s.slug}`);
    } catch (e) {
      toast(e.message, "error");
    }
  };
  return (
    <AdminScreen title="Hairstyles & tutorials" testId="admin-hairstyles">
      <label className="relative block">
        <span className="sr-only">Search hairstyles</span>
        <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-mute" aria-hidden />
        <input data-testid="admin-hairstyles-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search hairstyles…" className="field-input !min-h-[48px] !rounded-xl !border-[#D1D5DB] !pl-10" />
      </label>
      <form className="flex items-end gap-2" onSubmit={(e) => { e.preventDefault(); if (newName.trim()) create(); }}>
        <div className="flex-1"><Field testId="admin-hairstyles-new-name" label="New hairstyle" value={newName} onChange={setNewName} placeholder="e.g. Textured Fringe" /></div>
        <Button type="submit" testId="admin-hairstyles-create" title="Add" icon={Plus} disabled={!newName.trim()} className="!min-h-[44px]" />
      </form>
      {!items ? <Spinner /> : (
        <>
          <p data-testid="admin-hairstyles-count" className="t-caption">{items.length} styles · {items.filter((s) => s.archived).length} archived</p>
          <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
            {items.map((s) => (
              <button key={s.slug} type="button" data-testid={`admin-style-${s.slug}`} onClick={() => navigate(`/admin/hairstyle/${s.slug}`)} className={`focus-ring flex min-h-[72px] items-center gap-3 rounded-xl border border-line bg-off p-2 text-left transition-all hover:border-ocean hover:shadow-card ${s.archived ? "opacity-55" : ""}`}>
                {s.image_url ? <img src={s.image_url} alt="" loading="lazy" className="h-14 w-14 shrink-0 rounded-md object-cover" /> : <span className="h-14 w-14 shrink-0 rounded-md bg-tert" />}
                <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="t-strong truncate">{s.name}</span>
                  <span className="t-caption truncate">{(s.suitable_hair_types ?? []).join(", ")} · {s.styling_time} min · {s.primary_product}</span>
                  <span className="flex gap-1">
                    {s.archived ? <Tag label="Archived" tone="navy" /> : null}
                    {s.video_url ? <Tag label="Video" /> : null}
                  </span>
                </span>
              </button>
            ))}
          </div>
        </>
      )}
    </AdminScreen>
  );
}
